import { ComponentType, ReactNode, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

import { CourseAdminCard } from '@/components/shared/CourseAdminCard';
import {
  CardDragglableProps,
  CardStaticProps,
} from '@/components/shared/CourseAdminCard/types';
import Activity, { ActivityBlockType } from '@/models/Activity';
import { Breakpoints } from '@/utils/Breakpoint';
import ActivityBlockInlineForm from './ActivityBlockInlineForm';
import ActivityTypeBadge from './ActivityTypeBadge';
import QuestionBankButton from './QuestionBankButton';

export type ActivityInlineFormProps = {
  activity: Activity;
  onSubmit: (data: Partial<Activity>) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
};

export type ActivityBlockCardProps = {
  activity: Activity;
  onDelete: (activityId: number) => void;
  onUpdate?: (params: {
    activityId: number;
    data: Partial<Activity>;
  }) => Promise<Activity>;
  isUpdating?: boolean;
  leftAddon?: ReactNode;
  actionsAddon?: ReactNode;
  children?: ReactNode;
  InlineForm?: ComponentType<ActivityInlineFormProps>;
  isDragging?: boolean;
  isDeleting?: boolean;
  className?: string;
} & (CardStaticProps | CardDragglableProps);

const DefaultInlineForm = ({
  activity,
  onSubmit,
  onCancel,
  isLoading,
}: ActivityInlineFormProps) => (
  <ActivityBlockInlineForm
    defaultValues={{
      name: activity.name,
      activityType: activity.activityType ?? ActivityBlockType.REGULAR,
    }}
    onSubmit={data => onSubmit(data)}
    onCancel={onCancel}
    isLoading={isLoading}
  />
);

export default function ActivityBlockCard({
  activity,
  onDelete,
  onUpdate,
  isUpdating,
  leftAddon,
  actionsAddon,
  children,
  InlineForm = DefaultInlineForm,
  ...cardProps
}: ActivityBlockCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const isXl = useMediaQuery({ minWidth: Breakpoints.xl });

  if (isEditing) {
    return (
      <InlineForm
        activity={activity}
        onSubmit={async data => {
          await onUpdate?.({ activityId: activity.id, data });
          setIsEditing(false);
        }}
        onCancel={() => setIsEditing(false)}
        isLoading={isUpdating}
      />
    );
  }

  return (
    <CourseAdminCard variant="block" {...cardProps}>
      <CourseAdminCard.Header>
        <CourseAdminCard.Leading>
          <CourseAdminCard.Title>{activity.name}</CourseAdminCard.Title>
          {leftAddon ?? (
            <ActivityTypeBadge
              type={activity.activityType ?? ActivityBlockType.REGULAR}
              size={isXl ? 'md' : 'sm'}
            />
          )}
        </CourseAdminCard.Leading>
        <CourseAdminCard.Actions>
          {actionsAddon}
          <QuestionBankButton />
          {onUpdate && (
            <CourseAdminCard.EditButton onClick={() => setIsEditing(true)} />
          )}
          <CourseAdminCard.MoreActions>
            <CourseAdminCard.MoreActions.Delete
              onClick={() => onDelete(activity.id)}
              confirm
            />
          </CourseAdminCard.MoreActions>
        </CourseAdminCard.Actions>
      </CourseAdminCard.Header>
      {children}
    </CourseAdminCard>
  );
}
